import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { ConfigConstants } from '../../config/config-constants';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import { WindowService } from '../window/window.service';
import {
    EncryptionMigrator,
    EncryptionService,
    PREFIX_ENCRYPTED_CONTENT_LEGACY,
    UpgradableEncryption,
} from '@evorait/pace-encryption';
import { combineLatest, from, map, Observable, of } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class LocalStorageCommonService implements UpgradableEncryption {
    private readonly localStorage: Storage;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly window: WindowService,
        private readonly encryption: EncryptionService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly encryptionMigrator: EncryptionMigrator,
    ) {
        this.localStorage = this.window.nativeWindow.localStorage;
    }

    /**
     *
     * Checks if there are any entries using legacy encryption format.
     *
     * This method queries the collection of stored entries and determines whether
     * any entry's content starts with the legacy encryption prefix.
     *
     * This method is only relevant for the transition period from GW encryption
     * keys to Azure encryption keys. Once, all users that have on encryption
     * key on GW also have an encryption key on Azure, it can safely be removed.
     * ~ summer 2026 should be safe
     *
     * @returns {Observable<boolean>} An observable that emits `true` if at least one
     * entry uses legacy encryption, otherwise `false`.
     */
    public hasLegacyEncryption(): Observable<boolean> {
        return of(
            Object.entries(this.localStorage)
                .map(([, val]) => {
                    return val?.startsWith(PREFIX_ENCRYPTED_CONTENT_LEGACY);
                })
                .some((v) => !!v),
        );
    }

    /**
     * Upgrades all entries from legacy encryption to the current encryption format.
     *
     * This method performs a read-write transaction on the collection of stored entries.
     * It finds all entries whose content starts with the legacy prefix,
     * re-encrypts their content using the `encryptionMigrator`, and updates the entries
     * in the database.
     *
     * This method is only relevant for the transition period from GW encryption
     * keys to Azure encryption keys. Once, all users that have on encryption
     * key on GW also have an encryption key on Azure, it can safely be removed.
     * ~ summer 2026 should be safe
     *
     * @returns {Observable<boolean>} An observable that emits `true` once all legacy
     * encrypted entries have been successfully migrated.
     */
    public upgradeEncryption(): Observable<boolean> {
        const localStorageEntries = Object.entries(this.localStorage);

        if (localStorageEntries.length < 1) {
            return of(true);
        }

        return combineLatest(
            localStorageEntries.map(([key, val]) => {
                if (val.startsWith(PREFIX_ENCRYPTED_CONTENT_LEGACY)) {
                    return from(this.encryptionMigrator.reEncrypt(val)).pipe(
                        map((encrypted) => {
                            this.localStorage.setItem(key, encrypted);
                            return true;
                        }),
                    );
                }
                return of(true);
            }),
        ).pipe(map((results) => results.every((v) => v)));
    }

    /**
     * Central function to access the local storage. Any write actions on the local storage should use this function.
     * This enables us to replace the localStorage by a different data storage provider such as any database.
     *
     * @param id the identifier used as key to store the value in localStorage
     * @param value the value to be stored in localStorage
     */
    public async storageSetAsync(id: string, value: unknown): Promise<void> {
        return this.encryption.encryptAsync(JSON.stringify(value)).then((data) => this.localStorage.setItem(id, data));
    }

    /**
     * This function is only to be used to set some simple, unencrypted values before initialization. Only use if absolutely required!
     * Normal request to localStorage should use the `storageSet` function.
     *
     * @param id the identifier used as key to store the value in localStorage
     * @param value the value as string to be stored in localStorage
     */
    public preInitializationStorageSet(id: string, value: string): void {
        this.localStorage.setItem(id, value);
    }

    /**
     * Central function to access the local storage. Any read actions on the local storage should use this function.
     * This enables us to replace the localStorage by a different data storage provider such as any database.
     *
     * If the desired value does not exist, the value from localStorage will be 'null'. This is subsequently changed to
     * 'undefined' to keep 'null' out of the codebase.
     *
     * If the desired value was found, it will be returned with type annotation 'unknown'. This means that any access to
     * any property will only be possible after the existence of that property was ensured by a suitable check in the
     * code.
     *
     * @param id
     */
    public async storageGetAsync<T>(id: string): Promise<T | undefined> {
        const value = this.localStorage.getItem(id);
        if (value === null) {
            return undefined;
        }

        return this.encryption.decryptAsync(value).then((decrypted) => <T>JSON.parse(decrypted));
    }

    /**
     * This function is only to be used to read some simple, unencrypted values before initialization. Only use if absolutely required!
     * Normal request to localStorage should use the `storageSet` function.
     *
     * @param id the identifier used as key to store the value in localStorage
     */
    public preInitializationStorageGet(id: string): string | null {
        return this.localStorage.getItem(id);
    }

    /**
     * Central function to access the local storage. Any read actions on the local storage should use this function.
     * This enables us to replace the localStorage by a different data storage provider such as any database.
     *
     * @param id
     */
    public storageRemove(id: string): void {
        return this.localStorage.removeItem(id);
    }

    /**
     * Return all available keys in the local storage. Any object stored within this local storage instance will be
     * listed here with its key.
     */
    public storageKeys(): string[] {
        return Object.keys(this.localStorage);
    }

    public makeId(id: NotificationIdentifierModel): string {
        const constSeparator = ConfigConstants.unixFileNameSeparator;
        return id.getFrontId() + constSeparator + id.getQmnum() + constSeparator + id.getManum();
    }

    /**
     * Sums up the size of all keys currently stored in localStorage
     * To read the correct local storage memory size, we are reading out the raw strings from local storage.
     * It is on purpose that we are not using the storageGet() function of this service, because it would decrypt and
     * parse JSON objects. That process would change the storage size.
     */
    public printConsumedLocalStorageSize(): void {
        const kilobyteSize = 1024;
        const utf16Factor = 2;
        const maxStringSize = 50;
        const numberOfDigits = 2;
        const totalSize = this.storageKeys()
            .map((key) => {
                const value = this.localStorage.getItem(key);
                const retrievedValueLength = value !== null ? value.length : 0;
                const length = (key.length + retrievedValueLength) * utf16Factor;
                this.log.debug(
                    `${key.substring(0, maxStringSize)}: ${(length / kilobyteSize).toFixed(numberOfDigits)} KiB`,
                );
                return length;
            })
            .reduce((acc, length) => acc + length, 0);
        this.log.debug(`Total Size: ${(totalSize / kilobyteSize).toFixed(numberOfDigits)} KiB`);
    }
}
